import { useOktaAuth } from "@okta/okta-react";
import { AxiosError } from "axios";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { IJobDetails } from "../../api/jobsService/IJobsService";
import jobsServiceApi from "../../api/jobsService/jobsServiceApi";
import useJobServiceApi from "../../api/jobsService/useJobServiceApi";
import { ConfigContext } from "../../context/ConfigContext";

const useJobsDownloadStatus = (
  accountId: string,
  jobIds: string[]
): {
  downloadStatuses: IJobDetails[];
  error: AxiosError | undefined;
} => {
  const { authState } = useOktaAuth();
  const {
    JOBS_SERVICE_URL,
    POLLING_INTERVAL = 5000,
    ENABLE_RBAC_AUTH_SUPPORT,
    ENABLE_RBAC_AUTH_SUPPORT_FOR_JOBS_SERVICE,
  } = useContext(ConfigContext);
  const [statuses, setStatuses] = useState<IJobDetails[]>([]);
  const [error, setError] = useState<AxiosError | undefined>(undefined);
  const [accountIdInState, setAccountIdInState] = useState<string>(accountId);

  const { getStatuses } = useJobServiceApi();

  const isJobsApiAuthEnable =
    ENABLE_RBAC_AUTH_SUPPORT && ENABLE_RBAC_AUTH_SUPPORT_FOR_JOBS_SERVICE;

  const accessToken = useMemo(() => {
    if (authState && authState.isAuthenticated && authState.accessToken) {
      return authState.accessToken.accessToken;
    }
    return undefined;
  }, [authState?.isAuthenticated, authState?.accessToken]);

  if (accountId !== accountIdInState) {
    // To reset statuses when accountId changes for CPM users
    setAccountIdInState(accountId);
    setStatuses([]);
  }

  const pollHandler = useCallback(
    (token: string | undefined) => {
      if (jobIds.length === 0 || token === undefined) {
        return Promise.resolve([]);
      }

      const fetchPromise = isJobsApiAuthEnable
        ? getStatuses({ jobIds }, accountId)
        : jobsServiceApi.getStatuses(
            JOBS_SERVICE_URL,
            { jobIds },
            accountId,
            token
          );

      return fetchPromise
        .then(({ statuses: fetchedStatuses }) => {
          setError(undefined);
          setStatuses([...fetchedStatuses]);
        })
        .catch((e: AxiosError) => {
          setError(e);
          setStatuses([]);
        });
    },
    [jobIds]
  );

  useEffect(() => {
    const interval = setInterval(
      () => pollHandler(accessToken),
      POLLING_INTERVAL
    );
    return () => clearInterval(interval);
  }, [jobIds, accessToken]);

  return { downloadStatuses: statuses, error };
};

export default useJobsDownloadStatus;
